import { appReset, appResetSteps, onError } from '@actions/app.action';
import {
  AvailabilityActionTypes,
  availabilityInvalidSearchByQueryParams,
  availabilitySearchAllHome,
  availabilitySearchAllModify,
  availabilitySearchFromDestinations,
  availabilitySellTripSelectionSuccess,
} from '@actions/availability.action';
import {
  BagActionTypes,
  bagsSellBagSsrsCheckInSuccess,
  bagsSellBagSsrsSuccess,
} from '@actions/bags.action';
import {
  bookingCancelFlightsSuccess,
  bookingCheckInFlights,
  bookingDeleteBookingSuccess,
  bookingManageBooking,
  bookingGetBookingSuccess,
  BookingActionTypes,
  bookingTravelerChange,
  bookingUpgradeCabin,
  bookingPaymentsAcceptedBookingUpdated,
  bookingFurtherPaymentRequired,
  bookingAddBags,
  bookingViewChangeSeats,
  bookingViewChangeMeals,
  bookingAddExtras,
  bookingPaymentsAcceptedSuccess,
  bookingPaymentsDeclined,
  bookingGetBookingAfterPassengerUpdateSuccess,
} from '@actions/booking.action';
import {
  checkInAutoAssignSeatsSuccess,
  checkInCheckInPassengersSuccess,
} from '@actions/check-in.action';
import { mealsSellMealsSsrSuccess } from '@actions/meals.action';
import {
  navigateToPage,
  navigateToPreviousPathStep,
  navigateToNewCulture,
} from '@actions/navigate.action';
import { updatePassengersSuccess } from '@actions/passenger.action';
import { updatePassengerSeatsSuccess } from '@actions/seat.action';
import { tripDetailsPayBalance } from '@actions/trip-details.action';
import { userCompleteResetPasswordSuccess } from '@actions/user.action';
import { Location } from '@angular/common';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { BottomImageModalComponent } from '@components/modals/bottom-image-modal/bottom-image-modal.component';
import {
  CultureKeyType,
  CULTURE_ROUTE_PARAM,
  FlowBaseUrlEnum,
  MODAL_DIALOG_CENTER_LG,
} from '@constants/app.constants';
import { ErrorType } from '@models/app.models';
import { FlowPathEnum } from '@models/flow-wrapper.model';
import { GrowthBookFeatureKeys } from '@models/growthbook.models';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import {
  selectPathStepsNext,
  selectPathStepsPrevious,
} from '@selectors/app.selector';
import { selectBooking, selectConfirmationNumber, selectCurrentlyActiveJourney } from '@selectors/booking.selector';
import { selectAppCmsCopy, selectPaymentCopy } from '@selectors/cms.selector';
import {
  selectAppFlowPathName,
  selectIsCheckInPath,
  selectRouteParam,
} from '@selectors/router.selector';
import { GrowthBookService } from '@services/growthbook/growthbook.service';
import { ModalService } from '@services/modal/modal.service';
import { getRootPath } from '@utils/data-utils';
import { Observable, of } from 'rxjs';
import { first, map, mergeMap, tap } from 'rxjs/operators';
import { MultiAppService } from '@services/multiapp/multiapp.service';

@Injectable()
export class NavigateEffect {
  constructor(
    private actions$: Actions,
    private router: Router,
    private store: Store,
    private modalService: ModalService,
    private location: Location,
    private multiAppService: MultiAppService,
    public gbs: GrowthBookService,
  ) {}

  get currentFlow(): string {
    return getRootPath(this.router.url);
  }

  readonly gbf = GrowthBookFeatureKeys;

  search$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(availabilitySearchAllHome, availabilitySearchAllModify, availabilitySearchFromDestinations),
        concatLatestFrom(
          () =>
            this.store.select(
              selectRouteParam(CULTURE_ROUTE_PARAM),
            ) as Observable<CultureKeyType | undefined>,
        ),
        tap(([action, culture]) => {
          const nextFlow =
            action.type === AvailabilityActionTypes.searchAllModify
              ? FlowBaseUrlEnum.MODIFY
              : FlowBaseUrlEnum.BOOK;

          const flow = culture ? `${culture}/${nextFlow}` : nextFlow;

          this.router.navigate([flow, FlowPathEnum.FLIGHTS]);
        }),
      );
    },
    { dispatch: false },
  );

  manageBooking$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(bookingManageBooking, bookingAddBags),
      map(() => {
        return navigateToPage({
          flow: FlowBaseUrlEnum.MODIFY,
          page: FlowPathEnum.BAGS,
        });
      }),
    );
  });

  viewChangeSeats$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(bookingViewChangeSeats),
      map(() => {
        return navigateToPage({
          flow: FlowBaseUrlEnum.MODIFY,
          page: FlowPathEnum.SEATS,
        });
      }),
    );
  });

  viewChangeMeals$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(bookingViewChangeMeals),
      map(() => {
        return navigateToPage({
          flow: FlowBaseUrlEnum.MODIFY,
          page: FlowPathEnum.MEALS,
        });
      }),
    );
  });

  addExtras$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(bookingAddExtras),
      map(() => {
        return navigateToPage({
          flow: FlowBaseUrlEnum.MODIFY,
          page: FlowPathEnum.EXTRAS,
        });
      }),
    );
  });

  upgradeCabin$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(bookingUpgradeCabin),
      map(() => {
        return navigateToPage({
          flow: FlowBaseUrlEnum.UPGRADE,
          page: FlowPathEnum.FLIGHTS,
        });
      }),
    );
  });

  paymentsAccepted$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(bookingPaymentsAcceptedBookingUpdated),
      concatLatestFrom(() => [
        this.store.select(selectIsCheckInPath),
        this.store.select(selectRouteParam(CULTURE_ROUTE_PARAM)) as Observable<
          CultureKeyType | undefined
        >,
      ]),
      mergeMap(([action, isCheckIn, culture]) => {
        const hasRemainingBalance =
          (action.booking.breakdown?.balanceDue ?? 0) > 0;
        // if there is still a remaining balance on the booking,
        // navigate back to payment page
        if (hasRemainingBalance) {
          this.store
            .select(selectPaymentCopy)
            .pipe(first((c) => !!c))
            .subscribe((copy) => {
              this.modalService.show(BottomImageModalComponent, {
                class: MODAL_DIALOG_CENTER_LG,
                initialState: {
                  btnText: copy?.okayCopy,
                  text: copy?.paymentAcceptedCopy,
                  img: 'https://assets.flynorse.com/static/site/modals/three-tails.jpg',
                  altText: copy?.norsePlanesCopy,
                },
              });
            });
          const flow = culture
            ? `${culture}/${this.currentFlow}`
            : this.currentFlow;
          this.router.navigate([
            flow,
            isCheckIn ? FlowPathEnum.REVIEW : FlowPathEnum.PAYMENT,
          ]);
          return of(bookingFurtherPaymentRequired());
        }
        return of(bookingPaymentsAcceptedSuccess());
      }),
    );
  });

  navigateToPreviousPathStep$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(navigateToPreviousPathStep),
        concatLatestFrom(() => [
          this.store.select(selectPathStepsPrevious),
          this.store.select(
            selectRouteParam(CULTURE_ROUTE_PARAM),
          ) as Observable<CultureKeyType | undefined>,
        ]),
        tap(([_, previousStep, culture]) => {
          const flow = culture
            ? `${culture}/${this.currentFlow}`
            : this.currentFlow;

          if (previousStep) {
            this.router.navigate([flow, previousStep.name]);
          } else {
            if (this.currentFlow.startsWith(FlowBaseUrlEnum.MODIFY)) {
              const flow = culture ? `${culture}/${FlowBaseUrlEnum.TRIPS}` : FlowBaseUrlEnum.TRIPS;
              this.router.navigate([flow, FlowPathEnum.TRIP_DETAILS]);
            } else {
              this.location.back();
            }
          }
        }),
      );
    },
    { dispatch: false },
  );

  navigateToNextPathStep$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(
          // payment complete
          bookingPaymentsAcceptedSuccess,
          // other actions
          availabilitySellTripSelectionSuccess,
          bagsSellBagSsrsSuccess,
          mealsSellMealsSsrSuccess,
          bookingGetBookingSuccess,
          bookingGetBookingAfterPassengerUpdateSuccess,
          updatePassengersSuccess,
          updatePassengerSeatsSuccess,
          bagsSellBagSsrsCheckInSuccess,
          // updatePassengersDocumentsSuccess auto assigns seats and calls checkInAutoAssignSeatsSuccess,
          checkInAutoAssignSeatsSuccess,
        ),
        concatLatestFrom(() => [
          this.store.select(selectPathStepsNext),
          this.store.select(
            selectRouteParam(CULTURE_ROUTE_PARAM),
          ) as Observable<CultureKeyType | undefined>,
        ]),
        tap(([action, nextStep, culture]) => {
          const flow = culture
            ? `${culture}/${this.currentFlow}`
            : this.currentFlow;
          
          if (
            action.type === BookingActionTypes.getBookingAfterPassengerUpdateSuccess && 
            !action.navigate
          ) {
            return;
          }

          if (action['redirectToPayment']) {
            this.router.navigate([flow, FlowPathEnum.PAYMENT], {
              queryParamsHandling: 'preserve',
            });
            return;
          }
          if (
            action.type === AvailabilityActionTypes.sellTripSelectionSuccess &&
            action.isUpgraded
          ) {
            this.router.navigate([flow, action.redirect ? FlowPathEnum.SEATS : FlowPathEnum.BAGS], {
              queryParamsHandling: 'preserve',
            })
            return;
          }
          switch (action.type) {
            case BookingActionTypes.bookingPaymentsAcceptedSuccess:
              // go directly to confirmation page to bypass active step issue with Android App
              // if attempting to remove verify 3DS challenge returns you to app and completes the booking
              this.router.navigate([flow, FlowPathEnum.CONFIRMATION]);
              break;
            case BookingActionTypes.getBookingSuccess:
              if (nextStep && action.navigate) {
                this.router.navigate([flow, nextStep.name]);
              }
              break;
            case BagActionTypes.sellBagSsrsSuccess:
              if (action.redirect && nextStep) {
                this.router.navigate([flow, nextStep.name]);
              }
              break;
            default:
              if (nextStep) {
                this.router.navigate([flow, nextStep.name]);
              }
              break;
          }
        }),
      );
    },
    { dispatch: false },
  );

  tripDetailsPayBalance$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(tripDetailsPayBalance),
        concatLatestFrom(
          () =>
            this.store.select(
              selectRouteParam(CULTURE_ROUTE_PARAM),
            ) as Observable<CultureKeyType | undefined>,
        ),
        tap(([, culture]) => {
          const flow = culture
            ? `${culture}/${FlowBaseUrlEnum.PAY}`
            : FlowBaseUrlEnum.PAY;

          this.router.navigate([flow, FlowPathEnum.PAYMENT]);
        }),
      );
    },
    {
      dispatch: false,
    },
  );

  paymentsFailed$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(onError, bookingPaymentsDeclined),
        concatLatestFrom(() => [
          this.store.select(selectIsCheckInPath),
          this.store.select(
            selectRouteParam(CULTURE_ROUTE_PARAM),
          ) as Observable<CultureKeyType | undefined>,
          this.store.select(selectPaymentCopy),
          this.store.select(selectConfirmationNumber),
        ]),
        tap(
          ([
            {
              error: { type },
            },
            isCheckIn,
            culture,
            paymentCopy,
            recordLocator,
          ]) => {
            const cultureWithFlow = culture
              ? `${culture}/${this.currentFlow}`
              : this.currentFlow;
            if (type === ErrorType.BookingPaymentsDeclined) {
              this.router.navigate([
                cultureWithFlow,
                isCheckIn ? FlowPathEnum.REVIEW : FlowPathEnum.PAYMENT,
              ]);
            } else if (type === ErrorType.AutoAssignSeatsFailure) {
              this.router.navigate([cultureWithFlow, FlowPathEnum.BAGS]);
            } else if (type === ErrorType.BookingPollingFailure) {
              this.modalService.show(BottomImageModalComponent, {
                class: MODAL_DIALOG_CENTER_LG,
                initialState: {
                  btnText: paymentCopy?.okayCopy,
                  text: paymentCopy?.processingTimeoutError.replace(
                    '{pnr}',
                    recordLocator ?? '',
                  ),
                  img: 'https://assets.flynorse.com/static/site/modals/three-tails.jpg',
                  altText: paymentCopy?.norsePlanesCopy,
                },
              });
              this.router.navigate([cultureWithFlow, FlowPathEnum.PAYMENT]);
            } else if (type === ErrorType.BookingRefundFailure && this.currentFlow === FlowBaseUrlEnum.CANCEL.toString()) {
              this.router.navigate([cultureWithFlow, FlowPathEnum.PAYMENT]);
            } else if (type === ErrorType.BookingRefundFailure) {
              this.router.navigate([
                cultureWithFlow,
                FlowPathEnum.CONFIRMATION,
              ]);
            }
          },
        ),
      );
    },
    { dispatch: false },
  );

  bookingFetchFailed$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(onError),
        concatLatestFrom(() => this.store.select(selectAppCmsCopy)),
        tap(
          ([
            {
              error: { type },
            },
            copy,
          ]) => {
            if (type === ErrorType.BookingFetchFailure) {
              this.modalService.show(BottomImageModalComponent, {
                class: MODAL_DIALOG_CENTER_LG,
                initialState: {
                  btnText: copy?.okayCopy,
                  text: copy?.fetchBookingFailedCopy,
                  img: 'https://assets.flynorse.com/static/site/modals/three-tails.jpg',
                  altText: copy?.norsePlanesCopy,
                },
              });
              this.router.navigate(['/']);
            }
          },
        ),
      );
    },
    {
      dispatch: false,
    },
  );

  checkInFlights$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(bookingCheckInFlights),
        concatLatestFrom(
          () =>
            this.store.select(
              selectRouteParam(CULTURE_ROUTE_PARAM),
            ) as Observable<CultureKeyType | undefined>,
        ),
        tap(([, culture]) => {
          const flow = culture
            ? `${culture}/${FlowBaseUrlEnum.CHECK_IN}`
            : FlowBaseUrlEnum.CHECK_IN;

          this.router.navigate([flow, FlowPathEnum.DOCUMENTS]);
        }),
      );
    },
    { dispatch: false },
  );

  travelerChange$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(bookingTravelerChange),
        concatLatestFrom(
          () =>
            this.store.select(
              selectRouteParam(CULTURE_ROUTE_PARAM),
            ) as Observable<CultureKeyType | undefined>,
        ),
        tap(([, culture]) => {
          const flow = culture
            ? `${culture}/${FlowBaseUrlEnum.MODIFY}`
            : FlowBaseUrlEnum.MODIFY;

          this.router.navigate([flow, FlowPathEnum.TRAVELERS]);
        }),
      );
    },
    { dispatch: false },
  );

  cancelFlightsSuccess$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(bookingCancelFlightsSuccess),
        concatLatestFrom(
          () =>
            this.store.select(
              selectRouteParam(CULTURE_ROUTE_PARAM),
            ) as Observable<CultureKeyType | undefined>,
        ),
        tap(([, culture]) => {
          const flow = culture
            ? `${culture}/${FlowBaseUrlEnum.CANCEL}`
            : FlowBaseUrlEnum.CANCEL;

          this.router.navigate([flow, FlowPathEnum.PAYMENT]);
        }),
      );
    },
    { dispatch: false },
  );

  appReset$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(appReset, appResetSteps),
        concatLatestFrom(() => [this.store.select(selectAppFlowPathName)]),
        tap(([, flow]) => {
          // we navigate even if the page is confirmation because the only
          // way the modal could have been thrown on confirmation is if
          // they initiated an API request after the token expired by
          // clicking a button or some other element we haven't disabled.
          switch (flow) {
            case FlowBaseUrlEnum.ACCOUNT:
            case FlowBaseUrlEnum.BOOK: {
              this.router.navigate(['/'], { replaceUrl: true });
              break;
            }
            case FlowBaseUrlEnum.CANCEL:
            case FlowBaseUrlEnum.CHECK_IN:
            case FlowBaseUrlEnum.MODIFY:
            case FlowBaseUrlEnum.PAY:
            case FlowBaseUrlEnum.TRIPS:
            case FlowBaseUrlEnum.UPGRADE: {
              this.router.navigate(['/', FlowBaseUrlEnum.TRIPS], {
                replaceUrl: true,
              });
              break;
            }
            default:
              break;
          }
        }),
      );
    },
    { dispatch: false },
  );

  navigateHomeWithReplace$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(bookingDeleteBookingSuccess, userCompleteResetPasswordSuccess, availabilityInvalidSearchByQueryParams),
        tap(() => {
          this.router.navigate(['/'], { replaceUrl: true });
        }),
      );
    },
    { dispatch: false },
  );

  navigateToPage$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(navigateToPage),
        concatLatestFrom(() => [
          this.store.select(
            selectRouteParam(CULTURE_ROUTE_PARAM),
          ) as Observable<CultureKeyType | undefined>,
        ]),
        tap(([action, culture]) => {
          const cultureWithFlow = culture
            ? `${culture}/${action.flow}`
            : action.flow;

          this.router.navigate([cultureWithFlow, action.page]);
        }),
      );
    },
    { dispatch: false },
  );

  navigateToBoardingPasses$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(checkInCheckInPassengersSuccess),
        concatLatestFrom(
          () => [
            this.store.select(
              selectRouteParam(CULTURE_ROUTE_PARAM),
            ) as Observable<CultureKeyType | undefined>,
          ]
        ),
        tap(([action, culture]) => {
          if (action.navigate) {
            const flow = culture
              ? `${culture}/${this.currentFlow}`
              : this.currentFlow;

            this.router.navigate([flow, FlowPathEnum.BOARDING_PASS]);
          }
        }),
      );
    },
    { dispatch: false },
  );

  navigateToNewCulture$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(navigateToNewCulture),
        concatLatestFrom(
          () =>
            this.store.select(
              selectRouteParam(CULTURE_ROUTE_PARAM),
            ) as Observable<CultureKeyType | undefined>,
        ),
        tap(([action, currentCulture]) => {
          this.router.navigateByUrl(
            this.router.url.replace(`/${currentCulture}`, `/${action.newCulture}`),
          );
        }),
      );
    },
    { dispatch: false },
  );
}
